import {
  AlertCircle,
  ChevronRight,
  Languages,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  Fragment,
  useEffect,
  useState,
  type ComponentType,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { DappIcon } from './DappPickerDialog';
import {
  statusBadgeToneClass,
  statusTone,
  type StatusTone,
} from './questWorkspace';

export type SelectOption = {
  value: string;
  label: string;
};

export type FieldConfig<T extends object> = {
  key: keyof T & string;
  label: string;
  kind?: 'input' | 'textarea' | 'select';
  inputType?: string;
  options?: SelectOption[];
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  hint?: string;
  rows?: number;
  mono?: boolean;
  /**
   * Grid span override. Defaults to full-width for textareas and a single
   * column for everything else; set `'half'` to pack a textarea into one
   * column so two can sit side by side.
   */
  span?: 'full' | 'half';
  translation?: {
    metadataKey: keyof T & string;
    i18nField: string;
  };
};

export type FieldGroup<T extends object> = {
  title: string;
  description?: string;
  fields: FieldConfig<T>[];
};

export type PatchHandler = (
  key: 'seasons' | 'rounds' | 'apps' | 'quests' | 'credentials',
  id: string,
  patch: Record<string, unknown>,
) => void;

export type Crumb = {
  key: string;
  label: string;
  onClick: () => void;
};

/** Shared field-control tokens — one source of truth for input styling. */
export const fieldBaseClassName =
  'w-full rounded-lg border border-neutral-800 bg-neutral-950/40 text-sm text-neutral-100 [color-scheme:dark] placeholder:text-neutral-500 transition-colors hover:border-neutral-700 focus:border-teal-500/70 focus:outline-none focus:ring-2 focus:ring-teal-500/15';
export const inputClassName = `h-10 px-3 ${fieldBaseClassName}`;
export const textareaClassName = `px-3 py-2.5 ${fieldBaseClassName}`;

/**
 * Minimal button treatment for the quests toolbars — a thin border and a
 * transparent fill instead of the heavier global `outline` variant. Layer
 * accent colours on top with a trailing className.
 */
export const minimalButtonClassName =
  'border border-neutral-800 bg-transparent text-neutral-300 shadow-none hover:border-neutral-700 hover:bg-neutral-800/60 hover:text-white';
const invalidFieldClassName =
  'border-rose-500/60 focus:border-rose-500/70 focus:ring-rose-500/15';

/**
 * The single emphasized action in an editor header (e.g. "Run selection
 * preview", "Fill from catalog"). A teal-tinted treatment that reads as the
 * lead without competing with the global teal Save button in the page header.
 */
export const primaryActionButtonClassName =
  'border border-teal-500/40 bg-teal-500/10 text-teal-200 shadow-none hover:border-teal-400/60 hover:bg-teal-500/15 hover:text-teal-100';

/**
 * One pill shape for every status signal — the round status, the derived round
 * date state, the Disabled marker, and the Overview count chips all route
 * through this so the chips never drift apart visually.
 */
export function StatusBadge({
  tone,
  icon,
  className,
  children,
  ...rest
}: {
  tone: StatusTone;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
} & HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium',
        statusBadgeToneClass[tone],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </span>
  );
}

/** A single shimmering placeholder block. */
export function Skeleton(props: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('animate-pulse rounded bg-neutral-800/80', props.className)}
    />
  );
}

const NAV_SKELETON_ROWS = [
  'w-28',
  'ml-3 w-24',
  'ml-6 w-32',
  'ml-9 w-20',
  'ml-9 w-28',
  'ml-6 w-24',
  'ml-3 w-20',
  'w-24',
];

/** Placeholder for the workspace tree while the workspace loads. */
export function WorkspaceNavSkeleton() {
  return (
    <div
      aria-hidden="true"
      className="flex flex-col gap-2 rounded-xl border border-neutral-800/60 bg-neutral-900/30 p-2.5"
    >
      <Skeleton className="h-8 w-full" />
      <div className="flex gap-1.5">
        <Skeleton className="h-6 w-16" />
        <Skeleton className="h-6 w-20" />
      </div>
      <div className="mt-2 space-y-2">
        {NAV_SKELETON_ROWS.map((width, index) => (
          <div key={index} className="flex items-center gap-2">
            <Skeleton className="size-3.5 shrink-0 rounded" />
            <Skeleton className={cn('h-3.5', width)} />
          </div>
        ))}
      </div>
    </div>
  );
}

function SectionSkeleton(props: { fields: number }) {
  return (
    <div className="overflow-hidden rounded-xl border border-neutral-800/60 bg-neutral-900/30">
      <div className="border-b border-neutral-800/60 bg-neutral-800/40 px-5 py-3">
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="grid gap-x-4 gap-y-5 p-5 sm:grid-cols-2">
        {Array.from({ length: props.fields }).map((_, index) => (
          <div key={index} className="space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-10 w-full rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Placeholder for the detail editor while the workspace loads. */
export function EditorSkeleton() {
  return (
    <div aria-hidden="true" className="space-y-5">
      <div className="flex items-center gap-2.5">
        <Skeleton className="size-9 rounded-lg" />
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>
      <SectionSkeleton fields={4} />
      <SectionSkeleton fields={2} />
    </div>
  );
}

/** Placeholder rows for an async table (e.g. completion requests). */
export function TableSkeleton(props: { rows?: number; columns?: number }) {
  const rows = props.rows ?? 4;
  const columns = props.columns ?? 5;
  const widths = ['w-40', 'w-28', 'w-32', 'w-24', 'w-36'];
  return (
    <div aria-hidden="true" className="space-y-3">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex items-center gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton
              key={colIndex}
              className={cn('h-4', widths[colIndex % widths.length])}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function fieldValue<T extends object>(item: T, key: keyof T & string) {
  return String(item[key] ?? '');
}

function FieldError(props: { id: string; message?: string }) {
  if (!props.message) {
    return null;
  }
  return (
    <p id={props.id} className="flex items-start gap-1 text-xs text-rose-300">
      <AlertCircle className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
      <span>{props.message}</span>
    </p>
  );
}

function FieldShell(props: {
  label: string;
  id: string;
  required?: boolean;
  hint?: string;
  error?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  const errorId = `${props.id}-error`;
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={props.id}
          className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-neutral-300"
        >
          {props.label}
          {props.required && (
            <span className="text-rose-400" aria-hidden="true">
              *
            </span>
          )}
          {props.hint && (
            <span className="font-normal text-neutral-500">· {props.hint}</span>
          )}
        </label>
        {props.action}
      </div>
      {props.children}
      <FieldError id={errorId} message={props.error} />
    </div>
  );
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function translationJson<T extends object>(item: T, field: FieldConfig<T>) {
  const translation = field.translation;
  if (!translation) {
    return '{}';
  }

  try {
    const metadata = readRecord(
      JSON.parse(String(item[translation.metadataKey] ?? '{}')),
    );
    const i18n = readRecord(metadata.i18n);
    return JSON.stringify(readRecord(i18n[translation.i18nField]), null, 2);
  } catch {
    return '{}';
  }
}

type TranslationStatusView = {
  state: 'pending' | 'complete' | 'error';
  completedLanguageCount: number | null;
  languageCount: number | null;
  error: string | null;
} | null;

function translationStatus<T extends object>(
  item: T,
  field: FieldConfig<T>,
): TranslationStatusView {
  const translation = field.translation;
  if (!translation) {
    return null;
  }

  try {
    const metadata = readRecord(
      JSON.parse(String(item[translation.metadataKey] ?? '{}')),
    );
    const status = readRecord(
      readRecord(metadata.i18nStatus)[translation.i18nField],
    );
    const state =
      status.state === 'pending' ||
      status.state === 'complete' ||
      status.state === 'error'
        ? status.state
        : null;
    if (!state) {
      return null;
    }

    const completedLanguageCount =
      typeof status.completedLanguageCount === 'number'
        ? status.completedLanguageCount
        : null;
    const languageCount =
      typeof status.languageCount === 'number' ? status.languageCount : null;

    return {
      state,
      completedLanguageCount,
      languageCount,
      error: typeof status.error === 'string' ? status.error : null,
    };
  } catch {
    return null;
  }
}

function translationStatusText(status: TranslationStatusView) {
  if (!status) {
    return null;
  }

  const countText =
    status.completedLanguageCount !== null && status.languageCount !== null
      ? ` (${status.completedLanguageCount}/${status.languageCount})`
      : '';

  if (status.state === 'pending') {
    return `Translating${countText}`;
  }
  if (status.state === 'complete') {
    return `Complete${countText}`;
  }

  return status.error ? `Error: ${status.error}` : 'Error';
}

function TranslationJsonButton<T extends object>(props: {
  item: T;
  field: FieldConfig<T>;
}) {
  const [open, setOpen] = useState(false);
  const status = translationStatus(props.item, props.field);
  const statusText = translationStatusText(status);

  if (!props.field.translation) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(minimalButtonClassName, 'h-7 px-2 text-xs')}
        onClick={() => setOpen(true)}
      >
        {status?.state === 'pending' ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : status?.state === 'error' ? (
          <AlertCircle className="size-3.5 text-rose-300" />
        ) : (
          <Languages className="size-3.5" />
        )}
        Translations
      </Button>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden border-neutral-800 bg-neutral-950 text-white">
        <DialogHeader>
          <DialogTitle>{props.field.label} translations</DialogTitle>
          <DialogDescription>
            metadata.i18n.{props.field.translation.i18nField}
            {statusText ? ` · ${statusText}` : ''}
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="overflow-hidden py-0">
          <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-lg border border-neutral-800 bg-neutral-950/80 p-4 font-mono text-xs leading-relaxed text-neutral-300">
            {translationJson(props.item, props.field)}
          </pre>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function WorkspaceField<T extends object>(props: {
  item: T;
  field: FieldConfig<T>;
  id: string;
  value: string;
  error?: string;
  action?: ReactNode;
  onChange: (value: string) => void;
  onBlur?: () => void;
}) {
  const { field } = props;
  const errorId = `${props.id}-error`;
  const action =
    props.action || field.translation ? (
      <div className="flex shrink-0 items-center gap-1.5">
        {props.action}
        <TranslationJsonButton item={props.item} field={field} />
      </div>
    ) : null;
  const shared = {
    id: props.id,
    'aria-label': field.label,
    'aria-invalid': props.error ? true : undefined,
    'aria-describedby': props.error ? errorId : undefined,
    onBlur: props.onBlur,
  };
  const locked = field.readOnly || field.disabled;

  let control: ReactNode;
  if (field.kind === 'select') {
    control = (
      <select
        {...shared}
        value={props.value}
        disabled={field.disabled}
        onChange={(event) => props.onChange(event.target.value)}
        className={cn(inputClassName, props.error && invalidFieldClassName)}
      >
        {(field.options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  } else if (field.kind === 'textarea') {
    control = (
      <textarea
        {...shared}
        value={props.value}
        readOnly={field.readOnly}
        disabled={field.disabled}
        onInput={
          locked
            ? undefined
            : (event) => props.onChange(event.currentTarget.value)
        }
        placeholder={field.placeholder}
        rows={field.rows ?? 4}
        className={cn(
          textareaClassName,
          field.mono && 'font-mono text-xs leading-relaxed',
          props.error && invalidFieldClassName,
          locked &&
            'cursor-default border-neutral-800 bg-neutral-950/60 text-neutral-300 focus:border-neutral-800 focus:ring-0',
        )}
      />
    );
  } else {
    control = (
      <input
        {...shared}
        type={field.inputType ?? 'text'}
        value={props.value}
        readOnly={field.readOnly}
        disabled={field.disabled}
        onInput={
          locked
            ? undefined
            : (event) => props.onChange(event.currentTarget.value)
        }
        placeholder={field.placeholder}
        className={cn(
          inputClassName,
          props.error && invalidFieldClassName,
          locked &&
            'cursor-default border-neutral-800 bg-neutral-950/60 text-neutral-300 focus:border-neutral-800 focus:ring-0',
        )}
      />
    );
  }

  return (
    <FieldShell
      id={props.id}
      label={field.label}
      required={field.required}
      hint={field.hint}
      error={props.error}
      action={action}
    >
      {control}
    </FieldShell>
  );
}

export function FieldGrid<T extends object>(props: {
  item: T;
  fields: FieldConfig<T>[];
  idPrefix: string;
  onChange: (key: keyof T & string, value: string) => void;
  onBlur?: (key: keyof T & string) => void;
  errors?: Record<string, string | undefined>;
  fieldActions?: Partial<Record<keyof T & string, ReactNode>>;
}) {
  return (
    <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2">
      {props.fields.map((field) => {
        const fullWidth = field.span
          ? field.span === 'full'
          : field.kind === 'textarea';
        return (
          <div
            key={field.key}
            className={fullWidth ? 'sm:col-span-2' : undefined}
          >
            <WorkspaceField
              item={props.item}
              field={field}
              id={`${props.idPrefix}-${field.key}`}
              value={fieldValue(props.item, field.key)}
              error={props.errors?.[field.key]}
              action={props.fieldActions?.[field.key]}
              onChange={(value) => props.onChange(field.key, value)}
              onBlur={
                props.onBlur ? () => props.onBlur?.(field.key) : undefined
              }
            />
          </div>
        );
      })}
    </div>
  );
}

export function EditorSection(props: {
  title: string;
  description?: string;
  accent?: boolean;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-neutral-800/60 bg-neutral-900/30">
      <header
        className={cn(
          'flex items-center justify-between gap-3 border-b border-neutral-800/60 bg-neutral-800/40 px-5 py-3',
          props.accent && 'border-l-2 border-l-teal-500/50',
        )}
      >
        <div className="min-w-0">
          <h3 className="text-[13px] font-semibold text-white">
            {props.title}
          </h3>
          {props.description && (
            <p className="mt-0.5 text-xs text-neutral-400">
              {props.description}
            </p>
          )}
        </div>
        {props.actions}
      </header>
      <div className="p-5">{props.children}</div>
    </section>
  );
}

export function AdvancedSection(props: {
  title?: string;
  description?: string;
  children: ReactNode;
}) {
  const title = props.title ?? 'Advanced';
  const description = props.description ?? 'JSON and metadata';

  return (
    <details className="group overflow-hidden rounded-xl border border-neutral-800/60 bg-neutral-900/30">
      <summary className="flex cursor-pointer list-none items-center gap-2 px-5 py-3 text-[13px] font-medium text-neutral-300 transition-colors hover:text-white [&::-webkit-details-marker]:hidden">
        <ChevronRight
          className="size-3.5 shrink-0 text-neutral-500 transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
        {title}
        {description && (
          <span className="text-xs font-normal text-neutral-500">
            {description}
          </span>
        )}
      </summary>
      <div className="border-t border-neutral-800/60 p-5">{props.children}</div>
    </details>
  );
}

export function FieldGroups<T extends object>(props: {
  item: T;
  groups: FieldGroup<T>[];
  idPrefix: string;
  onChange: (key: keyof T & string, value: string) => void;
  onBlur?: (key: keyof T & string) => void;
  errors?: Record<string, string | undefined>;
  fieldActions?: Partial<Record<keyof T & string, ReactNode>>;
}) {
  return (
    <>
      {props.groups.map((group) => (
        <EditorSection
          key={group.title}
          title={group.title}
          description={group.description}
        >
          <FieldGrid
            item={props.item}
            fields={group.fields}
            idPrefix={props.idPrefix}
            onChange={props.onChange}
            onBlur={props.onBlur}
            errors={props.errors}
            fieldActions={props.fieldActions}
          />
        </EditorSection>
      ))}
    </>
  );
}

export function ChildrenPanel(props: {
  title: string;
  addLabel: string;
  icon: ComponentType<{ className?: string }>;
  emptyLabel?: string;
  items: Array<{ id: string; label: string; iconUrl?: string }>;
  onOpen: (id: string) => void;
  onAdd: () => void;
}) {
  const Icon = props.icon;

  return (
    <EditorSection title={`${props.title} · ${props.items.length}`}>
      <div className="flex flex-wrap items-center gap-2">
        {props.items.length === 0 && (
          <span className="text-xs text-neutral-500">
            {props.emptyLabel ?? `No ${props.title.toLowerCase()} yet.`}
          </span>
        )}
        {props.items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => props.onOpen(item.id)}
            className="group flex max-w-64 items-center gap-2 rounded-lg border border-neutral-800 bg-neutral-900/60 px-3 py-1.5 text-sm text-neutral-200 transition-colors hover:border-neutral-600 hover:bg-neutral-800/60 hover:text-white"
          >
            {item.iconUrl ? (
              <DappIcon
                url={item.iconUrl}
                name={item.label}
                className="size-4 rounded"
              />
            ) : (
              <Icon className="size-3.5 shrink-0 text-neutral-400 transition-colors group-hover:text-teal-300" />
            )}
            <span className="truncate">{item.label}</span>
            <ChevronRight className="size-3 shrink-0 text-neutral-500 opacity-0 transition-opacity group-hover:opacity-100" />
          </button>
        ))}
        <button
          type="button"
          onClick={props.onAdd}
          className="flex items-center gap-1.5 rounded-lg border border-teal-500/30 bg-teal-500/5 px-3 py-1.5 text-sm font-medium text-teal-300 transition-colors hover:border-teal-500/50 hover:bg-teal-500/10"
        >
          <Plus className="size-3.5" />
          {props.addLabel}
        </button>
      </div>
    </EditorSection>
  );
}

function RemoveButton(props: {
  onRemove: () => void;
  cascadeSummary?: string;
}) {
  const [armed, setArmed] = useState(false);

  useEffect(() => {
    if (!armed) {
      return;
    }

    const timer = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(timer);
  }, [armed]);

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={cn(
          minimalButtonClassName,
          'min-w-[8.5rem] justify-center',
          armed &&
            'border-rose-500/50 text-rose-300 hover:border-rose-400 hover:bg-rose-500/10 hover:text-rose-200',
        )}
        onClick={() => {
          if (armed) {
            props.onRemove();
            return;
          }
          setArmed(true);
        }}
      >
        <Trash2 className="size-4" />
        {armed ? 'Confirm remove' : 'Remove'}
      </Button>
      <span className="sr-only" role="status" aria-live="polite">
        {armed ? 'Click confirm remove to delete this item.' : ''}
      </span>
      {armed && props.cascadeSummary && (
        <span className="max-w-[16rem] text-right text-[11px] leading-tight text-rose-300/80">
          Also deletes {props.cascadeSummary}
        </span>
      )}
    </div>
  );
}

export function EditorFrame(props: {
  breadcrumb?: Crumb[];
  title: ReactNode;
  kindLabel: string;
  icon?: ReactNode;
  status?: string;
  extraBadges?: ReactNode;
  disabled?: boolean;
  inheritedDisabled?: boolean;
  primaryAction?: ReactNode;
  actions?: ReactNode;
  onRemove?: () => void;
  removeCascadeSummary?: string;
  children: ReactNode;
}) {
  const tone = statusTone(props.status);
  const hasLeadingActions = Boolean(props.primaryAction || props.actions);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {props.breadcrumb && props.breadcrumb.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-1 text-xs text-neutral-400">
              {props.breadcrumb.map((crumb, index) => (
                <Fragment key={crumb.key}>
                  {index > 0 && (
                    <ChevronRight
                      className="size-3 text-neutral-600"
                      aria-hidden="true"
                    />
                  )}
                  <button
                    type="button"
                    onClick={crumb.onClick}
                    className="max-w-40 truncate rounded transition-colors hover:text-neutral-100"
                  >
                    {crumb.label}
                  </button>
                </Fragment>
              ))}
            </div>
          )}
          <div className="flex min-w-0 items-center gap-2.5">
            {props.icon && (
              <span className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900/60 text-teal-300">
                {props.icon}
              </span>
            )}
            <h2
              data-editor-heading
              tabIndex={-1}
              className="truncate text-lg font-semibold text-white outline-none"
            >
              {props.title}
            </h2>
            <span className="shrink-0 rounded-full border border-neutral-800 bg-neutral-900/60 px-2 py-0.5 text-xs text-neutral-300">
              {props.kindLabel}
            </span>
            {props.status && (
              <StatusBadge tone={tone}>{props.status}</StatusBadge>
            )}
            {props.extraBadges}
            {props.disabled && (
              <StatusBadge tone="danger">Disabled</StatusBadge>
            )}
          </div>
          {props.inheritedDisabled && !props.disabled && (
            <p className="mt-1.5 text-xs text-rose-300/80">
              Inactive — a parent is disabled.
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
          {props.primaryAction}
          {props.actions}
          {props.onRemove && (
            <>
              {hasLeadingActions && (
                <span
                  aria-hidden="true"
                  className="mx-0.5 hidden h-5 w-px bg-neutral-800 sm:block"
                />
              )}
              <RemoveButton
                onRemove={props.onRemove}
                cascadeSummary={props.removeCascadeSummary}
              />
            </>
          )}
        </div>
      </div>
      {props.children}
    </div>
  );
}
